import { z } from 'zod';
import { ABSENCE_LEVELS, ATTENDANCE_MARKS } from '../attendance';
import { PageQuerySchema, pageOf } from './common';

export const CLASS_KINDS = ['CM', 'TD', 'TP'] as const;
export type ClassKind = (typeof CLASS_KINDS)[number];
export const JUSTIFICATION_REASONS = ['medical', 'administrative', 'bereavement', 'other'] as const;
export type JustificationReason = (typeof JUSTIFICATION_REASONS)[number];
export const JUSTIFICATION_MAX_BYTES = 5 * 1024 * 1024;

const RollCallSummarySchema = z.object({
  at: z.string(),
  byName: z.string().nullable(),
  present: z.number().int(),
  absent: z.number().int(),
  late: z.number().int(),
});

export const ClassSessionSchema = z.object({
  id: z.uuid(),
  courseId: z.uuid(),
  courseName: z.string(),
  groupId: z.uuid(),
  /** « GL2 Groupe B » parts, formatted by the client. */
  levelCode: z.string(),
  groupName: z.string(),
  kind: z.enum(CLASS_KINDS),
  room: z.string().nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  teacherName: z.string().nullable(),
  students: z.number().int(),
  rollCall: RollCallSummarySchema.nullable(),
  /** Marked « enseignant absent » (timetable): nothing to call. */
  teacherAbsent: z.boolean(),
});
export type ClassSession = z.infer<typeof ClassSessionSchema>;

/** Teacher's roll-call home: sessions of the day, past ones still without roll call, recent ones. */
export const MySessionsSchema = z.object({
  today: z.array(ClassSessionSchema),
  pending: z.array(ClassSessionSchema),
  recent: z.array(ClassSessionSchema),
  /** Courses × groups the user teaches (to open an extra session). */
  teaching: z.array(
    z.object({
      courseId: z.uuid(),
      courseName: z.string(),
      groupId: z.uuid(),
      levelCode: z.string(),
      groupName: z.string(),
    }),
  ),
});
export type MySessions = z.infer<typeof MySessionsSchema>;

export const SessionCreateSchema = z
  .object({
    courseId: z.uuid(),
    groupId: z.uuid(),
    kind: z.enum(CLASS_KINDS),
    room: z.string().trim().max(40).optional(),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
  })
  .refine((s) => Date.parse(s.endsAt) > Date.parse(s.startsAt), {
    path: ['endsAt'],
    message: 'attendance.errors.endBeforeStart',
  })
  .refine((s) => Date.parse(s.endsAt) - Date.parse(s.startsAt) <= 6 * 3_600_000, {
    path: ['endsAt'],
    message: 'attendance.errors.tooLong',
  });
export type SessionCreate = z.infer<typeof SessionCreateSchema>;

export const RollCallSchema = z.object({
  session: ClassSessionSchema,
  students: z.array(
    z.object({
      studentId: z.uuid(),
      matricule: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      mark: z.enum(ATTENDANCE_MARKS).nullable(),
      justified: z.boolean(),
    }),
  ),
  canEdit: z.boolean(),
  /** Teachers can correct until then (null = no limit for the administration). */
  editableUntil: z.string().nullable(),
  /** Absent students are told they have this many hours to hand in a justification. */
  justificationDelayHours: z.number().int(),
});
export type RollCall = z.infer<typeof RollCallSchema>;

export const RollCallSaveSchema = z.object({
  marks: z
    .array(z.object({ studentId: z.uuid(), mark: z.enum(ATTENDANCE_MARKS) }))
    .min(1)
    .max(300),
});
export type RollCallSave = z.infer<typeof RollCallSaveSchema>;
export const RollCallResultSchema = RollCallSummarySchema;

// ── Monitoring (ScrAbsences) ────────────────────────────────────────────────
export const AbsenceQuerySchema = PageQuerySchema.extend({
  level: z.enum(['all', ...ABSENCE_LEVELS]).default('all'),
  programId: z.uuid().optional(),
});
export type AbsenceQuery = z.infer<typeof AbsenceQuerySchema>;

export const AbsenceRowSchema = z.object({
  studentId: z.uuid(),
  matricule: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  levelCode: z.string().nullable(),
  groupName: z.string().nullable(),
  courseId: z.uuid(),
  courseName: z.string(),
  absentHours: z.number(),
  volumeHours: z.number(),
  pct: z.number(),
  level: z.enum(ABSENCE_LEVELS),
  /** Already notified for the current level. */
  notified: z.boolean(),
});
export type AbsenceRow = z.infer<typeof AbsenceRowSchema>;

export const AbsencePageSchema = pageOf(AbsenceRowSchema).extend({
  counts: z.object({
    all: z.number().int(),
    elim: z.number().int(),
    alert: z.number().int(),
    watch: z.number().int(),
  }),
  kpis: z.object({
    isoWeek: z.number().int(),
    weekHours: z.number(),
    weekJustifiedHours: z.number(),
    /** Absent hours / expected student-hours of the week, in percent (null without sessions). */
    rate: z.number().nullable(),
    previousRate: z.number().nullable(),
  }),
  /** Thresholds of the institution (settings), for the subtitle, KPIs and the bar marker. */
  rules: z.object({ eliminationPct: z.number().nullable(), alertPct: z.number(), watchPct: z.number() }),
});
export type AbsencePage = z.infer<typeof AbsencePageSchema>;

export const NotifyAbsencesSchema = z.object({
  /** Empty = every student in alert or eliminated who was not notified yet. */
  items: z.array(z.object({ studentId: z.uuid(), courseId: z.uuid() })).max(500),
});
export const NotifyResultSchema = z.object({ notified: z.number().int(), skipped: z.number().int() });

// ── One student's absences (record tab, justification dialog) ───────────────
export const AbsenceRecordSchema = z.object({
  id: z.uuid(),
  sessionId: z.uuid(),
  courseId: z.uuid(),
  courseName: z.string(),
  kind: z.enum(CLASS_KINDS),
  startsAt: z.string(),
  endsAt: z.string(),
  hours: z.number(),
  mark: z.enum(ATTENDANCE_MARKS),
  justificationId: z.uuid().nullable(),
});
export type AbsenceRecord = z.infer<typeof AbsenceRecordSchema>;

export const JustificationSchema = z.object({
  id: z.uuid(),
  reason: z.enum(JUSTIFICATION_REASONS),
  note: z.string().nullable(),
  status: z.enum(['pending', 'approved', 'rejected']),
  fileName: z.string().nullable(),
  fileSize: z.number().int().nullable(),
  hours: z.number(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
});
export type Justification = z.infer<typeof JustificationSchema>;

export const StudentAbsencesSchema = z.object({
  yearLabel: z.string().nullable(),
  absentHours: z.number(),
  justifiedHours: z.number(),
  lateCount: z.number().int(),
  courses: z.array(
    z.object({
      courseId: z.uuid(),
      courseName: z.string(),
      volumeHours: z.number(),
      absentHours: z.number(),
      justifiedHours: z.number(),
      pct: z.number(),
      level: z.enum(ABSENCE_LEVELS).nullable(),
    }),
  ),
  records: z.array(AbsenceRecordSchema),
  justifications: z.array(JustificationSchema),
  /** Absences covered by a justification the registrar has not checked yet. */
  pendingRecordIds: z.array(z.uuid()),
});
export type StudentAbsences = z.infer<typeof StudentAbsencesSchema>;

export const JustificationCreateSchema = z.object({
  reason: z.enum(JUSTIFICATION_REASONS),
  note: z.string().trim().max(300).optional(),
  recordIds: z.array(z.uuid()).min(1).max(200),
});
export type JustificationCreate = z.infer<typeof JustificationCreateSchema>;
